"use client"

import { type Connection, PublicKey, SystemProgram, Transaction } from "@solana/web3.js"
const SYSTEM_PROGRAM_ID = SystemProgram.programId
import { Program, AnchorProvider, BN, setProvider } from "@coral-xyz/anchor"
import { TOKEN_PROGRAM_ID, getAssociatedTokenAddress, createAssociatedTokenAccountInstruction, getAccount, createTransferInstruction } from "@solana/spl-token"
// @ts-ignore - Types file may not exist until anchor build is run

// Load IDL dynamically for browser environment
let IDL: any = null
let IDL_LOADING: Promise<any> | null = null

// Async function to load IDL from public path
async function loadIDL(): Promise<any> {
  if (IDL) return IDL
  
  if (IDL_LOADING) return IDL_LOADING

  IDL_LOADING = (async () => {
    try {
      // Try to load from public path (Next.js serves files from public/ or can access from API)
      // Option 1: Load from API endpoint (recommended)
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001"
      let response = await fetch(`${apiUrl}/api/idl`)
      
      if (!response.ok) {
        // Option 2: Try loading from public folder
        response = await fetch("/irma_subscriptions.json")
      }
      
      if (!response.ok) {
        // Option 3: Try loading from /idl path
        response = await fetch("/idl/irma_subscriptions.json")
      }
      
      if (!response.ok) {
        throw new Error(`Failed to load IDL: ${response.status} ${response.statusText}`)
      }
      
      IDL = await response.json()
      IDL_LOADING = null
      return IDL
    } catch (error: any) {
      IDL_LOADING = null
      console.error("Failed to load IDL:", error)
      throw new Error(`IDL file not found. Make sure the IDL is accessible. Error: ${error.message}`)
    }
  })()

  return IDL_LOADING
}

// Fix IDL: Anchor needs accounts to have type definitions with size information
function fixIDL(idl: any): any {
  if (!idl.accounts || !idl.types) return idl

  const typeMap = new Map<string, any>()
  idl.types.forEach((type: any) => {
    if (type.name && type.type) {
      typeMap.set(type.name, type.type)
    }
  })

  // Calculate sizes for each account type
  const calculateSize = (type: any): number => {
    if (!type || !type.fields) return 8 // Default discriminator size
    
    let size = 8 // Discriminator
    for (const field of type.fields) {
      if (field.type === "pubkey") {
        size += 32
      } else if (field.type === "u64") {
        size += 8
      } else if (field.type === "u32") {
        size += 4
      } else if (field.type === "u8") {
        size += 1
      } else if (field.type === "bool") {
        size += 1
      } else if (field.type === "string") {
        // String is 4 bytes (length) + actual string length
        size += 4 + 32 // Default string size
      }
    }
    return size
  }

  // Anchor 0.32 expects accounts to have a 'type' field that references the type definition
  idl.accounts = idl.accounts.map((account: any) => {
    if (account.name) {
      const accountType = typeMap.get(account.name)
      if (accountType && accountType.kind === "struct" && accountType.fields) {
        const size = calculateSize(accountType)
        
        return {
          ...account,
          type: {
            kind: "struct",
            fields: accountType.fields,
            size: size
          },
          size: size
        }
      }
    }
    return account
  })
  
  return idl
}

// Program ID from Anchor.toml - will be updated from IDL when loaded
export const IRMA_PROGRAM_ID = new PublicKey("Dvi7eAZqc5N2KExDQXYsJZdE8Bo36CqVbzHNHCGK5MNn")

// PDA Seeds
export const SUBSCRIPTION_VAULT_SEED = Buffer.from("subscription_vault")
export const WITHDRAWAL_VAULT_SEED = Buffer.from("withdrawal_vault")
export const PLAN_SEED = Buffer.from("plan")
export const SUBSCRIPTION_SEED = Buffer.from("subscription")
export const AFFILIATE_SEED = Buffer.from("affiliate")

export class IRMASubscriptionClient {
  program: any
  connection: Connection
  private _idl: any = null
  private _initPromise: Promise<void> | null = null
  private _programId: PublicKey | null = null
  private _wallet: any = null

  constructor(connection: Connection, wallet: any) {
    this.connection = connection
    this._wallet = wallet
    
    // Initialize program asynchronously
    this._initPromise = this.initializeProgram(connection, wallet)
  }

  private async initializeProgram(connection: Connection, wallet: any): Promise<void> {
    try {
      // Ensure wallet has required properties
      if (!wallet || !wallet.publicKey) {
        throw new Error("Wallet must have a publicKey property")
      }
      
      if (!wallet.publicKey) {
        throw new Error("Wallet publicKey is required")
      }
      
      const provider = new AnchorProvider(connection, wallet, {
        commitment: "confirmed",
      })
      setProvider(provider)

      // Load IDL
      const rawIdl = await loadIDL()
      this._idl = fixIDL(rawIdl)

      if (!this._idl || !this._idl.address || !this._idl.instructions) {
        throw new Error("Invalid IDL structure. Missing required fields (address, instructions)")
      }

      // Initialize program
      try {
        console.log("Initializing Program with IDL address:", this._idl.address)
        console.log("IDL Program ID:", this._idl.address)
        console.log("Hardcoded Program ID:", IRMA_PROGRAM_ID.toString())
        console.log("IDL instructions count:", this._idl.instructions?.length || 0)
        console.log("IDL accounts count:", this._idl.accounts?.length || 0)
        
        // Validate IDL address matches expected program ID
        if (this._idl.address !== IRMA_PROGRAM_ID.toString()) {
          console.warn(`⚠️  IDL address (${this._idl.address}) doesn't match hardcoded Program ID (${IRMA_PROGRAM_ID.toString()})`)
          console.warn("⚠️  Using IDL address as the source of truth")
        }
        
        // Validate accounts have proper structure
        if (this._idl.accounts) {
          const invalidAccounts = this._idl.accounts.filter((acc: any) => !acc.type || !acc.size)
          if (invalidAccounts.length > 0) {
            console.warn("⚠️  Some accounts missing type/size:", invalidAccounts.map((a: any) => a.name))
          }
        }
        
        // Use the program ID from IDL address (this is the actual deployed program ID)
        const programIdFromIdl = new PublicKey(this._idl.address)
        this._programId = programIdFromIdl
        // Program constructor: new Program(idl, provider) - program ID comes from idl.address
        this.program = new Program(this._idl as any, provider) as any
        console.log("✅ Program initialized successfully")
        console.log("Using Program ID from IDL:", programIdFromIdl.toString())
      } catch (programError: any) {
        console.error("Program initialization error:", programError)
        console.error("Error stack:", programError.stack)
        console.error("IDL structure:", {
          hasAddress: !!this._idl.address,
          hasInstructions: !!this._idl.instructions,
          instructionsCount: this._idl.instructions?.length,
          hasAccounts: !!this._idl.accounts,
          accountsCount: this._idl.accounts?.length,
          accountsWithTypes: this._idl.accounts?.filter((a: any) => a.type).length,
          accountsWithSizes: this._idl.accounts?.filter((a: any) => a.size).length,
          hasTypes: !!this._idl.types,
          typesCount: this._idl.types?.length,
        })
        
        // If the error is about size, Anchor's AccountClient is having issues with the IDL structure
        if (programError.message.includes("size") || programError.message.includes("undefined")) {
          console.log("⚠️  Anchor AccountClient size error detected")
          console.log("💡 This is a known issue with Anchor 0.32 IDL structure")
          console.log("💡 Attempting workaround: Program without accounts namespace")
          console.log("⚠️  Note: account.fetch() will use manual deserialization, but instructions will work")
          
          // Create IDL copy without accounts - this allows instructions to work
          const idlCopy = JSON.parse(JSON.stringify(this._idl))
          const { accounts, ...idlWithoutAccounts } = idlCopy
          
          try {
            // Use IDL address for program ID
            const programIdFromIdl = new PublicKey(this._idl.address)
            this._programId = programIdFromIdl
            // Program constructor: new Program(idl, provider) - program ID comes from idl.address
            this.program = new Program(idlWithoutAccounts as any, provider) as any
            console.log("✅ Program initialized successfully (accounts namespace disabled)")
            const programIdString = programIdFromIdl.toString()
            console.log("Using Program ID from IDL:", programIdString)
            // Store original IDL for manual deserialization
            (this as any)._idl = this._idl
          } catch (workaroundError: any) {
            console.error("Workaround failed:", workaroundError.message)
            throw new Error(`Failed to initialize Program: ${programError.message}. This appears to be an Anchor 0.32 compatibility issue. Consider: 1) Updating Anchor version, 2) Regenerating IDL with 'anchor build', or 3) Using Anchor workspace API instead of manual IDL loading.`)
          }
        } else {
          throw new Error(`Failed to initialize Program: ${programError.message}. Check IDL structure.`)
        }
      }
    } catch (error: any) {
      console.error("Error initializing IRMASubscriptionClient:", error)
      console.error("Stack:", error.stack)
      throw new Error(`Failed to initialize client: ${error.message}`)
    }
  }

  // Wait for program to be initialized
  private async ensureInitialized(): Promise<void> {
    if (this._initPromise) {
      await this._initPromise
    }
    if (!this.program) {
      throw new Error("Program not initialized. IDL may have failed to load.")
    }
  }

  // Helper: Get program ID (waits for initialization if needed)
  private getProgramId(): PublicKey {
    if (this._programId) {
      return this._programId
    }
    // Fallback to IRMA_PROGRAM_ID if not yet initialized
    return IRMA_PROGRAM_ID
  }

  // Helper: Get Subscription Vault PDA
  getSubscriptionVault(): PublicKey {
    const programId = this.getProgramId()
    const [vault] = PublicKey.findProgramAddressSync([SUBSCRIPTION_VAULT_SEED], programId)
    return vault
  }

  // Helper: Get Withdrawal Vault PDA
  getWithdrawalVault(): PublicKey {
    // Use PDA as the contract expects
    const programId = this.getProgramId()
    const [vault] = PublicKey.findProgramAddressSync([WITHDRAWAL_VAULT_SEED], programId)
    return vault
  }

  // Helper: Get Plan PDA
  getPlanPDA(platformId: string): PublicKey {
    const programId = this.getProgramId()
    const [plan] = PublicKey.findProgramAddressSync(
      [PLAN_SEED, Buffer.from(platformId)],
      programId
    )
    return plan
  }

  // Helper: Get Subscription PDA
  getSubscriptionPDA(subscriber: PublicKey): PublicKey {
    const programId = this.getProgramId()
    const [subscription] = PublicKey.findProgramAddressSync(
      [SUBSCRIPTION_SEED, subscriber.toBuffer()],
      programId
    )
    return subscription
  }

  // Helper: Get Affiliate Account PDA
  getAffiliateAccountPDA(affiliate: PublicKey): PublicKey {
    const programId = this.getProgramId()
    const [account] = PublicKey.findProgramAddressSync(
      [AFFILIATE_SEED, affiliate.toBuffer()],
      programId
    )
    return account
  }

  // SUBSCRIPTION FUNCTIONS
  async initializeSubscriptionVault(authority: PublicKey): Promise<string> {
    await this.ensureInitialized()
    const vault = this.getSubscriptionVault()
    const tx = await this.program.methods
      .initializeSubscriptionVault()
      .accounts({
        authority,
        subscriptionVault: vault,
        systemProgram: SystemProgram.programId,
      } as any)
      .rpc()
    return tx
  }

  async initializeWithdrawalVault(authority: PublicKey): Promise<string> {
    await this.ensureInitialized()
    const vault = this.getWithdrawalVault()
    
    try {
      // Try using .rpc() first
      const tx = await this.program.methods
        .initializeWithdrawalVault()
        .accounts({
          authority,
          withdrawalVault: vault,
          systemProgram: SystemProgram.programId,
        } as any)
        .rpc()
      return tx
    } catch (rpcError: any) {
      // If .rpc() fails, use manual signing
      if (rpcError.message?.includes("sign") || rpcError.message?.includes("Wallet")) {
        const { Transaction } = await import("@solana/web3.js")
        const initIx = await this.program.methods
          .initializeWithdrawalVault()
          .accounts({
            authority,
            withdrawalVault: vault,
            systemProgram: SystemProgram.programId,
          } as any)
          .instruction()
        
        const tx = new Transaction().add(initIx)
        const { blockhash } = await this.connection.getLatestBlockhash("confirmed")
        tx.recentBlockhash = blockhash
        tx.feePayer = authority
        
        if (!this._wallet?.signTransaction) {
          throw new Error("Wallet signTransaction method not available")
        }
        const signed = await this._wallet.signTransaction(tx)
        const signature = await this.connection.sendRawTransaction(signed.serialize(), {
          skipPreflight: false,
          maxRetries: 3,
        })
        await this.connection.confirmTransaction(signature, "confirmed")
        return signature
      }
      throw rpcError
    }
  }

  async createSubscriptionPlan(
    platformId: string,
    planName: string,
    priceInUsd: number, // Price in USD (e.g., 99.00)
    durationDays: number,
    authority: PublicKey,
  ): Promise<string> {
    await this.ensureInitialized()
    const planPDA = this.getPlanPDA(platformId)
    // Convert USD to cents (e.g., $99 = 9900 cents)
    const priceInCents = Math.floor(priceInUsd * 100)
    const tx = await this.program.methods
      .createSubscriptionPlan(platformId, planName, new BN(priceInCents), durationDays)
      .accounts({
        authority,
        subscriptionPlan: planPDA,
        systemProgram: SystemProgram.programId,
      } as any)
      .rpc()
    return tx
  }

  async closeSubscriptionPlan(
    platformId: string,
    authority: PublicKey,
  ): Promise<string> {
    await this.ensureInitialized()
    const planPDA = this.getPlanPDA(platformId)
    
    const tx = await this.program.methods
      .closeSubscriptionPlan(platformId)
      .accounts({
        subscriptionPlan: planPDA,
        authority,
      } as any)
      .rpc()
    return tx
  }

  // Get Price Oracle PDA
  getPriceOraclePDA(): PublicKey {
    const programId = this.getProgramId()
    const [priceOracle] = PublicKey.findProgramAddressSync(
      [Buffer.from("price_oracle")],
      programId
    )
    return priceOracle
  }

  // Get Admin Config PDA
  getAdminConfigPDA(): PublicKey {
    const programId = this.getProgramId()
    const [adminConfig] = PublicKey.findProgramAddressSync(
      [Buffer.from("admin_config")],
      programId
    )
    return adminConfig
  }

  // Fetch admin config
  async getAdminConfig(): Promise<any> {
    await this.ensureInitialized()
    try {
      const adminConfigPDA = this.getAdminConfigPDA()
      const adminConfig = await this.program.account.adminConfig.fetch(adminConfigPDA)
      // adminWithdrawalBps is u16, which Anchor deserializes as a number (not BN)
      const adminWithdrawalBps = adminConfig.adminWithdrawalBps ?? adminConfig.admin_withdrawal_bps
      const bpsValue = typeof adminWithdrawalBps === 'number' 
        ? adminWithdrawalBps 
        : (typeof adminWithdrawalBps?.toNumber === 'function' ? adminWithdrawalBps.toNumber() : Number(adminWithdrawalBps) || 0)
      
      return {
        authority: adminConfig.authority.toString(),
        adminWithdrawalBps: bpsValue,
        adminWithdrawalTokenAccount: (adminConfig.adminWithdrawalTokenAccount ?? adminConfig.admin_withdrawal_token_account)?.toString(),
        marketPoolTokenAccount: (adminConfig.marketPoolTokenAccount ?? adminConfig.market_pool_token_account)?.toString(),
      }
    } catch (error: any) {
      console.error("Error fetching admin config:", error)
      throw new Error(`Admin config not initialized: ${error.message}`)
    }
  }

  // Get current IRMA price from oracle
  async getIrmaPrice(): Promise<number | null> {
    await this.ensureInitialized()
    try {
      const priceOraclePDA = this.getPriceOraclePDA()
      const priceOracle = await this.program.account.priceOracle.fetch(priceOraclePDA)
      // Convert from 1e8 scale to decimal
      return Number(priceOracle.price) / 100_000_000
    } catch (error: any) {
      console.error("Error fetching IRMA price:", error)
      return null
    }
  }

  // Initialize price oracle (admin only)
  async initializePriceOracle(initialPrice: number, authority: PublicKey): Promise<string> {
    await this.ensureInitialized()
    const priceOraclePDA = this.getPriceOraclePDA()
    const priceScaled = Math.floor(initialPrice * 100_000_000)
    const tx = await this.program.methods
      .initializePriceOracle(new BN(priceScaled))
      .accounts({
        priceOracle: priceOraclePDA,
        authority,
        systemProgram: SystemProgram.programId,
      } as any)
      .rpc()
    return tx
  }

  // Initialize admin config (admin only)
  async initializeAdminConfig(
    authority: PublicKey,
    adminWithdrawalBps: number, // Basis points (e.g., 6000 = 60%)
    adminWithdrawalTokenAccount: PublicKey,
    marketPoolTokenAccount: PublicKey,
  ): Promise<string> {
    await this.ensureInitialized()
    const adminConfigPDA = this.getAdminConfigPDA()
    const tx = await this.program.methods
      .initializeAdminConfig(
        new BN(adminWithdrawalBps),
        adminWithdrawalTokenAccount,
        marketPoolTokenAccount,
      )
      .accounts({
        adminConfig: adminConfigPDA,
        authority,
        systemProgram: SystemProgram.programId,
      } as any)
      .rpc()
    return tx
  }

  // Update admin config (admin only)
  async updateAdminConfig(
    authority: PublicKey,
    adminWithdrawalBps?: number,
    adminWithdrawalTokenAccount?: PublicKey,
    marketPoolTokenAccount?: PublicKey,
  ): Promise<string> {
    await this.ensureInitialized()
    const adminConfigPDA = this.getAdminConfigPDA()
    
    const tx = await this.program.methods
      .updateAdminConfig(
        adminWithdrawalBps !== undefined ? new BN(adminWithdrawalBps) : null,
        adminWithdrawalTokenAccount || null,
        marketPoolTokenAccount || null,
      )
      .accounts({
        adminConfig: adminConfigPDA,
        authority,
      } as any)
      .rpc()
    return tx
  }

  async subscribe(
    subscriber: PublicKey,
    mint: PublicKey,
    irmaAmount: number,
    durationDays: number = 30,
    autoRenew: boolean = false,
  ): Promise<string> {
    await this.ensureInitialized()
    const subscriptionPDA = this.getSubscriptionPDA(subscriber)
    const vault = this.getSubscriptionVault()

    const subscriberATA = await getAssociatedTokenAddress(mint, subscriber)
    const vaultATA = await getAssociatedTokenAddress(mint, vault, true)

    // Check if subscription already exists
    try {
      const existingSubscription = await this.program.account.subscription.fetch(subscriptionPDA)
      const now = Date.now() / 1000
      const isExpired = now >= existingSubscription.expiryDate.toNumber()

      if (existingSubscription.isActive && !isExpired) {
        throw new Error(
          `You already have an active subscription. ` +
          `It expires on ${new Date(existingSubscription.expiryDate.toNumber() * 1000).toLocaleDateString()}. ` +
          `Please wait for it to expire or cancel it first.`
        )
      }

      // Subscription exists but is expired or cancelled, use renew instead
      console.log("Subscription account exists but is expired/cancelled. Using renew_subscription instead...")
      return await this.renewSubscription(subscriber, mint, irmaAmount, durationDays)
    } catch (err: any) {
      if (err.message?.includes("already have an active subscription")) {
        throw err
      }
      if (!err.message?.includes("Account does not exist") && !err.message?.includes("AccountNotInitialized")) {
        console.warn("Error checking existing subscription:", err.message)
      }
    }

    // Check if vault token account exists, if not, create it
    const vaultATAInfo = await this.connection.getAccountInfo(vaultATA)

    // Get admin config
    const adminConfigPDA = this.getAdminConfigPDA()
    let adminConfig
    try {
      adminConfig = await this.getAdminConfig()
    } catch (error: any) {
      throw new Error(`Admin config not initialized. Please contact admin to initialize admin configuration. ${error.message}`)
    }

    const adminWithdrawalTokenAccount = new PublicKey(adminConfig.adminWithdrawalTokenAccount)
    const marketPoolTokenAccount = new PublicKey(adminConfig.marketPoolTokenAccount)

    // Convert irmaAmount to smallest unit (9 decimals)
    const irmaAmountBN = new BN(Math.floor(irmaAmount * 1_000_000_000))

    const accountsObj = {
      subscriber,
      subscriberTokenAccount: subscriberATA,
      subscriptionVault: vault,
      vaultTokenAccount: vaultATA,
      subscription: subscriptionPDA,
      adminConfig: adminConfigPDA,
      adminWithdrawalTokenAccount: adminWithdrawalTokenAccount,
      marketPoolTokenAccount: marketPoolTokenAccount,
      tokenProgram: TOKEN_PROGRAM_ID,
      systemProgram: SystemProgram.programId,
    }

    // Build the subscribe instruction
    const subscribeIx = await this.program.methods
      .subscribe(irmaAmountBN, durationDays, autoRenew)
      .accounts(accountsObj as any)
      .instruction()

    // If vault ATA doesn't exist, add create instruction before subscribe
    if (!vaultATAInfo) {
      console.log("Vault token account not found, creating it...")
      const { Transaction } = await import("@solana/web3.js")
      const createATAInstruction = createAssociatedTokenAccountInstruction(
        subscriber,
        vaultATA,
        vault,
        mint
      )

      const tx = new Transaction().add(createATAInstruction, subscribeIx)
      const { blockhash } = await this.connection.getLatestBlockhash("confirmed")
      tx.recentBlockhash = blockhash
      tx.feePayer = subscriber

      if (!this._wallet?.signTransaction) {
        throw new Error("Wallet signTransaction method not available")
      }
      const signed = await this._wallet.signTransaction(tx)
      const signature = await this.connection.sendRawTransaction(signed.serialize(), {
        skipPreflight: false,
        maxRetries: 3,
      })
      await this.connection.confirmTransaction(signature, "confirmed")
      console.log("Subscription transaction (with ATA creation):", signature)
      return signature
    }

    // Vault ATA exists, just subscribe
    const tx = await this.program.methods
      .subscribe(irmaAmountBN, durationDays, autoRenew)
      .accounts(accountsObj as any)
      .rpc()
    return tx
  }

  async validateSubscription(subscriber: PublicKey): Promise<boolean> {
    await this.ensureInitialized()
    const subscriptionPDA = this.getSubscriptionPDA(subscriber)
    const result = await this.program.methods
      .validateSubscription()
      .accounts({
        subscription: subscriptionPDA,
      } as any)
      .view()
    return result as boolean
  }

  async enableAutoRenew(subscriber: PublicKey): Promise<string> {
    await this.ensureInitialized()
    const subscriptionPDA = this.getSubscriptionPDA(subscriber)
    const tx = await this.program.methods
      .enableAutoRenew()
      .accounts({
        subscription: subscriptionPDA,
        subscriber,
      } as any)
      .rpc()
    return tx
  }

  async disableAutoRenew(subscriber: PublicKey): Promise<string> {
    await this.ensureInitialized()
    const subscriptionPDA = this.getSubscriptionPDA(subscriber)
    const tx = await this.program.methods
      .disableAutoRenew()
      .accounts({
        subscription: subscriptionPDA,
        subscriber,
      } as any)
      .rpc()
    return tx
  }

  async cancelSubscription(subscriber: PublicKey): Promise<string> {
    await this.ensureInitialized()
    const subscriptionPDA = this.getSubscriptionPDA(subscriber)
    const tx = await this.program.methods
      .cancelSubscription()
      .accounts({
        subscription: subscriptionPDA,
        subscriber,
      } as any)
      .rpc()
    return tx
  }

  async renewSubscription(
    subscriber: PublicKey,
    mint: PublicKey,
    irmaAmount: number,
    durationDays: number = 30,
  ): Promise<string> {
    await this.ensureInitialized()
    const subscriptionPDA = this.getSubscriptionPDA(subscriber)
    const vault = this.getSubscriptionVault()
    const adminConfigPDA = this.getAdminConfigPDA()

    let adminConfig
    try {
      adminConfig = await this.getAdminConfig()
    } catch (error: any) {
      throw new Error(`Admin config not initialized. Please contact admin to initialize admin configuration. ${error.message}`)
    }

    const adminWithdrawalTokenAccount = new PublicKey(adminConfig.adminWithdrawalTokenAccount)
    const marketPoolTokenAccount = new PublicKey(adminConfig.marketPoolTokenAccount)

    const subscriberATA = await getAssociatedTokenAddress(mint, subscriber)
    const vaultATA = await getAssociatedTokenAddress(mint, vault, true)

    const irmaAmountBN = new BN(Math.floor(irmaAmount * 1_000_000_000))

    const tx = await this.program.methods
      .renewSubscription(irmaAmountBN, durationDays)
      .accounts({
        subscriber,
        subscriberTokenAccount: subscriberATA,
        subscriptionVault: vault,
        vaultTokenAccount: vaultATA,
        subscription: subscriptionPDA,
        adminConfig: adminConfigPDA,
        adminWithdrawalTokenAccount: adminWithdrawalTokenAccount,
        marketPoolTokenAccount: marketPoolTokenAccount,
        tokenProgram: TOKEN_PROGRAM_ID,
        systemProgram: SystemProgram.programId,
      } as any)
      .rpc()
    return tx
  }

  // AFFILIATE FUNCTIONS
  async initializeAffiliate(affiliate: PublicKey): Promise<string> {
    await this.ensureInitialized()
    const affiliatePDA = this.getAffiliateAccountPDA(affiliate)
    const tx = await this.program.methods
      .initializeAffiliate()
      .accounts({
        affiliate,
        affiliateAccount: affiliatePDA,
        systemProgram: SystemProgram.programId,
      } as any)
      .rpc()
    return tx
  }

  async accumulateCredits(affiliate: PublicKey, amount: number): Promise<string> {
    await this.ensureInitialized()
    const affiliatePDA = this.getAffiliateAccountPDA(affiliate)
    const tx = await this.program.methods
      .accumulateCredits(new BN(amount))
      .accounts({
        affiliate,
        affiliateAccount: affiliatePDA,
      } as any)
      .rpc()
    return tx
  }

  // WITHDRAWAL FUNCTIONS
  async isWithdrawalVaultInitialized(): Promise<boolean> {
    try {
      const vault = this.getWithdrawalVault()
      const accountInfo = await this.connection.getAccountInfo(vault)
      return accountInfo !== null && accountInfo.data.length > 0
    } catch (err: any) {
      // If account doesn't exist, it's not initialized
      if (err.message?.includes("Account does not exist") || 
          err.message?.includes("AccountNotInitialized") || 
          err.code === 3012) {
        return false
      }
      // For other errors, assume not initialized
      console.warn("Error checking withdrawal vault:", err.message)
      return false
    }
  }

  async withdrawRewards(
    affiliate: PublicKey,
    amount: number,
    mint: PublicKey,
  ): Promise<string> {
    await this.ensureInitialized()
    
    const affiliatePDA = this.getAffiliateAccountPDA(affiliate)
    const vault = this.getWithdrawalVault()
    
    // Check if withdrawal vault is initialized, if not, initialize it
    const isVaultInitialized = await this.isWithdrawalVaultInitialized()
    if (!isVaultInitialized) {
      console.log("Withdrawal vault not initialized, initializing now...")
      if (!this._wallet?.publicKey) {
        throw new Error("Wallet not connected. Please connect your wallet to initialize the withdrawal vault.")
      }
      try {
        await this.initializeWithdrawalVault(this._wallet.publicKey)
        console.log("✅ Withdrawal vault initialized successfully")
      } catch (err: any) {
        console.error("Failed to initialize withdrawal vault:", err)
        throw new Error(`Failed to initialize withdrawal vault: ${err.message}`)
      }
    }
    
    const vaultATA = await getAssociatedTokenAddress(mint, vault, true)
    const affiliateATA = await getAssociatedTokenAddress(mint, affiliate)
    
    console.log("💰 Withdrawal Details:")
    console.log("   Affiliate Wallet:", affiliate.toString())
    console.log("   Affiliate Token Account (destination):", affiliateATA.toString())
    console.log("   Vault Token Account (source):", vaultATA.toString())
    console.log("   Amount:", (amount / 1e9).toFixed(4), "IRMA")

    // Funding account address
    const FUNDING_ACCOUNT = new PublicKey("3wjJXZRRMsrTdnkNTV71w2ysoRgkaZH7DTxR47aenW4P")

    // Check if vault ATA exists and has balance
    let vaultATAInfo = await this.connection.getAccountInfo(vaultATA)
    const affiliateATAInfo = await this.connection.getAccountInfo(affiliateATA)
    
    // Check vault balance and fund if needed
    let vaultBalance = 0
    if (vaultATAInfo) {
      try {
        const vaultAccount = await getAccount(this.connection, vaultATA)
        vaultBalance = Number(vaultAccount.amount) / 1e9 // IRMA has 9 decimals
      } catch (err) {
        console.warn("Could not read vault balance:", err)
        // If account exists but we can't read it, it might be empty
        vaultBalance = 0
      }
    }
    
    const requestedAmount = amount / 1e9
    console.log(`Vault balance: ${vaultBalance.toFixed(4)} IRMA, Requested: ${requestedAmount.toFixed(4)} IRMA`)
    console.log(`Vault PDA: ${vault.toString()}, Vault Token Account (ATA): ${vaultATA.toString()}`)
    
    // If vault has sufficient balance, skip auto-funding
    if (vaultBalance >= requestedAmount) {
      console.log(`✅ Vault has sufficient balance (${vaultBalance.toFixed(4)} IRMA). Proceeding with withdrawal...`)
    } else {
      // Vault has insufficient balance
      console.log(`⚠️  Vault has insufficient balance. Current: ${vaultBalance.toFixed(4)} IRMA, Needed: ${requestedAmount.toFixed(4)} IRMA`)
      
      // Check if funding account is the connected wallet
      if (!this._wallet?.publicKey || !this._wallet.publicKey.equals(FUNDING_ACCOUNT)) {
        throw new Error(
          `Insufficient funds in withdrawal vault. ` +
          `Vault token account (${vaultATA.toString()}) has ${vaultBalance.toFixed(4)} IRMA, but ${requestedAmount.toFixed(4)} IRMA is needed. ` +
          `Please transfer IRMA tokens to the vault token account: ${vaultATA.toString()}`
        )
      }
      
      // Only proceed with auto-funding if the connected wallet is the funding account
      console.log("Attempting to fund vault from connected wallet...")
      
      const fundingNeeded = requestedAmount - vaultBalance
      const fundingAmountRaw = BigInt(Math.ceil(fundingNeeded * 1e9)) // Convert to smallest unit
      
      try {
        // Get funding account's token account
        const fundingATA = await getAssociatedTokenAddress(mint, FUNDING_ACCOUNT)
        const fundingAccountInfo = await this.connection.getAccountInfo(fundingATA)
        
        if (!fundingAccountInfo) {
          throw new Error(
            `Funding account (${FUNDING_ACCOUNT.toString()}) does not have a token account for this mint. ` +
            `Please create a token account for the funding account first.`
          )
        }
        
        const fundingAccount = await getAccount(this.connection, fundingATA)
        const fundingBalance = Number(fundingAccount.amount) / 1e9
        
        if (fundingBalance < fundingNeeded) {
          throw new Error(
            `Insufficient funds in funding account. ` +
            `Funding account has ${fundingBalance.toFixed(4)} IRMA, but ${fundingNeeded.toFixed(4)} IRMA is needed. ` +
            `Please fund the account ${FUNDING_ACCOUNT.toString()} first.`
          )
        }
        
        // Ensure vault ATA exists before transferring
        if (!vaultATAInfo) {
          console.log("Vault token account doesn't exist, creating it first...")
          const { Transaction } = await import("@solana/web3.js")
          const createVaultATAInstruction = createAssociatedTokenAccountInstruction(
            FUNDING_ACCOUNT, // payer
            vaultATA, // ATA address
            vault, // owner (PDA vault)
            mint // mint
          )
          
          const createTx = new Transaction().add(createVaultATAInstruction)
          const { blockhash: createBlockhash } = await this.connection.getLatestBlockhash("confirmed")
          createTx.recentBlockhash = createBlockhash
          createTx.feePayer = FUNDING_ACCOUNT
          
          const signedCreateTx = await this._wallet.signTransaction(createTx)
          const createSig = await this.connection.sendRawTransaction(signedCreateTx.serialize(), {
            skipPreflight: false,
            maxRetries: 3,
          })
          await this.connection.confirmTransaction(createSig, "confirmed")
          console.log("Vault token account created. Transaction:", createSig)
          
          // Update vault ATA info
          vaultATAInfo = await this.connection.getAccountInfo(vaultATA)
        }
        
        // Create transfer instruction to fund the vault
        const { Transaction } = await import("@solana/web3.js")
        const transferIx = createTransferInstruction(
          fundingATA, // source
          vaultATA, // destination
          FUNDING_ACCOUNT, // authority (signer)
          fundingAmountRaw // amount in smallest units
        )
        
        const fundTx = new Transaction().add(transferIx)
        const { blockhash } = await this.connection.getLatestBlockhash("confirmed")
        fundTx.recentBlockhash = blockhash
        fundTx.feePayer = FUNDING_ACCOUNT
        
        const signedFundTx = await this._wallet.signTransaction(fundTx)
        const fundSignature = await this.connection.sendRawTransaction(signedFundTx.serialize(), {
          skipPreflight: false,
          maxRetries: 3,
        })
        await this.connection.confirmTransaction(fundSignature, "confirmed")
        console.log("✅ Vault funded successfully. Transaction:", fundSignature)
        
        // Update vault ATA info after funding
        vaultATAInfo = await this.connection.getAccountInfo(vaultATA)
        const updatedVaultAccount = await getAccount(this.connection, vaultATA)
        vaultBalance = Number(updatedVaultAccount.amount) / 1e9
        console.log(`Updated vault balance: ${vaultBalance.toFixed(4)} IRMA`)
      } catch (fundError: any) {
        // If auto-funding fails, throw a clear error with instructions
        throw new Error(
          `Insufficient funds in withdrawal vault and auto-funding failed. ` +
          `Vault token account (${vaultATA.toString()}) has ${vaultBalance.toFixed(4)} IRMA, but ${requestedAmount.toFixed(4)} IRMA is needed. ` +
          `Error: ${fundError.message}. ` +
          `Please manually transfer IRMA tokens to vault token account: ${vaultATA.toString()}`
        )
      }
    }

    // Get provider for transaction building
    const provider = this.program.provider as AnchorProvider

    // Build the withdraw instruction
    const withdrawIx = await this.program.methods
      .withdrawRewards(new BN(amount))
      .accounts({
        affiliate,
        affiliateAccount: affiliatePDA,
        withdrawalVault: vault,
        vaultTokenAccount: vaultATA,
        affiliateTokenAccount: affiliateATA,
        tokenProgram: TOKEN_PROGRAM_ID,
        systemProgram: SystemProgram.programId,
      } as any)
      .instruction()

    // If vault ATA doesn't exist, create it first
    if (!vaultATAInfo) {
      console.log("Vault token account not found, creating it...")
      const { Transaction } = await import("@solana/web3.js")
      const createVaultATAInstruction = createAssociatedTokenAccountInstruction(
        affiliate, // payer (affiliate pays for ATA creation)
        vaultATA, // ATA address
        vault, // owner (PDA vault)
        mint // mint
      )
      
      // If affiliate ATA also doesn't exist, create both
      if (!affiliateATAInfo) {
        console.log("Affiliate token account not found, creating it...")
        const createAffiliateATAInstruction = createAssociatedTokenAccountInstruction(
          affiliate, // payer
          affiliateATA, // ATA address
          affiliate, // owner
          mint // mint
        )
        try {
          const { Transaction, VersionedTransaction } = await import("@solana/web3.js")
          const tx = new Transaction().add(createVaultATAInstruction, createAffiliateATAInstruction, withdrawIx)
          
          // Get recent blockhash
          const { blockhash } = await this.connection.getLatestBlockhash("confirmed")
          tx.recentBlockhash = blockhash
          tx.feePayer = affiliate
          
          // Sign and send transaction
          if (!this._wallet?.signTransaction) {
            throw new Error("Wallet signTransaction method not available")
          }
          const signed = await this._wallet.signTransaction(tx)
          const signature = await this.connection.sendRawTransaction(signed.serialize(), {
            skipPreflight: false,
            maxRetries: 3,
          })
          await this.connection.confirmTransaction(signature, "confirmed")
          console.log("Withdrawal transaction (with both ATA creations):", signature)
          return signature
        } catch (err: any) {
          if (err.message?.includes("insufficient funds") || err.logs?.some((log: string) => log.includes("insufficient funds"))) {
            throw new Error(
              `Insufficient funds in withdrawal vault. ` +
              `The vault does not have enough IRMA tokens to fulfill this withdrawal. ` +
              `Please contact the platform administrator to fund the withdrawal vault.`
            )
          }
          throw err
        }
      }
      
      // Only vault ATA needs creation
      try {
        const { Transaction } = await import("@solana/web3.js")
        const tx = new Transaction().add(createVaultATAInstruction, withdrawIx)
        
        // Get recent blockhash
        const { blockhash } = await this.connection.getLatestBlockhash("confirmed")
        tx.recentBlockhash = blockhash
        tx.feePayer = affiliate
        
        // Sign and send transaction
        if (!this._wallet?.signTransaction) {
          throw new Error("Wallet signTransaction method not available")
        }
        const signed = await this._wallet.signTransaction(tx)
        const signature = await this.connection.sendRawTransaction(signed.serialize(), {
          skipPreflight: false,
          maxRetries: 3,
        })
        await this.connection.confirmTransaction(signature, "confirmed")
        console.log("Withdrawal transaction (with vault ATA creation):", signature)
        return signature
      } catch (err: any) {
        if (err.message?.includes("insufficient funds") || err.logs?.some((log: string) => log.includes("insufficient funds"))) {
          throw new Error(
            `Insufficient funds in withdrawal vault. ` +
            `The vault does not have enough IRMA tokens to fulfill this withdrawal. ` +
            `Please contact the platform administrator to fund the withdrawal vault.`
          )
        }
        throw err
      }
    }

    // If only affiliate ATA doesn't exist, create it
    if (!affiliateATAInfo) {
      console.log("Affiliate token account not found, creating it...")
      const { Transaction } = await import("@solana/web3.js")
      const createAffiliateATAInstruction = createAssociatedTokenAccountInstruction(
        affiliate, // payer
        affiliateATA, // ATA address
        affiliate, // owner
        mint // mint
      )
      try {
        const { Transaction } = await import("@solana/web3.js")
        const tx = new Transaction().add(createAffiliateATAInstruction, withdrawIx)
        
        // Get recent blockhash
        const { blockhash } = await this.connection.getLatestBlockhash("confirmed")
        tx.recentBlockhash = blockhash
        tx.feePayer = affiliate
        
        // Sign and send transaction
        if (!this._wallet?.signTransaction) {
          throw new Error("Wallet signTransaction method not available")
        }
        const signed = await this._wallet.signTransaction(tx)
        const signature = await this.connection.sendRawTransaction(signed.serialize(), {
          skipPreflight: false,
          maxRetries: 3,
        })
        await this.connection.confirmTransaction(signature, "confirmed")
        console.log("Withdrawal transaction (with affiliate ATA creation):", signature)
        return signature
      } catch (err: any) {
        if (err.message?.includes("insufficient funds") || err.logs?.some((log: string) => log.includes("insufficient funds"))) {
          throw new Error(
            `Insufficient funds in withdrawal vault. ` +
            `The vault does not have enough IRMA tokens to fulfill this withdrawal. ` +
            `Please contact the platform administrator to fund the withdrawal vault.`
          )
        }
        throw err
      }
    }

    // Both ATAs exist, check vault balance before withdrawing
    try {
      const vaultAccount = await getAccount(this.connection, vaultATA)
      const vaultBalance = Number(vaultAccount.amount) / 1e9 // IRMA has 9 decimals
      const requestedAmount = amount / 1e9
      
      console.log(`Vault balance check: ${vaultBalance.toFixed(4)} IRMA available, ${requestedAmount.toFixed(4)} IRMA requested`)
      console.log(`Vault address: ${vault.toString()}`)
      console.log(`Vault token account (ATA): ${vaultATA.toString()}`)
      
      if (vaultBalance < requestedAmount) {
        throw new Error(
          `Insufficient funds in withdrawal vault. ` +
          `Vault (${vault.toString()}) has ${vaultBalance.toFixed(4)} IRMA, but ${requestedAmount.toFixed(4)} IRMA was requested. ` +
          `Please fund the vault token account (${vaultATA.toString()}) with IRMA tokens first.`
        )
      }
    } catch (err: any) {
      // If account doesn't exist or has no balance, throw a clearer error
      if (err.message?.includes("InvalidAccountData") || err.message?.includes("Account does not exist")) {
        throw new Error(
          `Withdrawal vault token account is empty or not funded. ` +
          `Vault address: ${vault.toString()}, Token account: ${vaultATA.toString()}. ` +
          `Please fund the vault token account with IRMA tokens before withdrawals can be processed.`
        )
      }
      // If it's already our custom error, re-throw it
      if (err.message?.includes("Insufficient funds")) {
        throw err
      }
      // For other errors, log and continue (might be a network issue)
      console.warn("Could not check vault balance:", err.message)
      console.log(`Vault address: ${vault.toString()}, Token account: ${vaultATA.toString()}`)
    }

    // Both ATAs exist, proceed with withdrawal
    try {
      // Use .rpc() which should handle signing via the provider
      // But if that fails, we'll fall back to manual signing
      try {
        const tx = await this.program.methods
          .withdrawRewards(new BN(amount))
          .accounts({
            affiliate,
            affiliateAccount: affiliatePDA,
            withdrawalVault: vault,
            vaultTokenAccount: vaultATA,
            affiliateTokenAccount: affiliateATA,
            tokenProgram: TOKEN_PROGRAM_ID,
            systemProgram: SYSTEM_PROGRAM_ID,
          } as any)
          .rpc()
        return tx
      } catch (rpcError: any) {
        // Check if it's a balance error first before trying manual signing
        const rpcErrorMessage = rpcError.message || ""
        const rpcErrorLogs = rpcError.logs || []
        const rpcAllLogs = rpcErrorLogs.join(" ")
        const rpcAnchorErrorCode = rpcError.error?.errorCode?.code || rpcError.code || rpcError.errorCode
        const rpcAnchorErrorName = rpcError.error?.errorCode?.name || rpcError.error?.name || rpcError.name
        
        // If it's an InsufficientBalance error, throw it with proper message
        if (
          rpcAnchorErrorCode === 6004 ||
          rpcAnchorErrorName === "InsufficientBalance" ||
          rpcAnchorErrorName === "insufficientBalance" ||
          rpcErrorMessage.includes("InsufficientBalance") ||
          rpcErrorMessage.includes("insufficient balance") ||
          rpcAllLogs.includes("InsufficientBalance") ||
          rpcAllLogs.includes("insufficient balance") ||
          (rpcErrorMessage.includes("6004") && rpcErrorMessage.includes("balance")) ||
          (rpcErrorMessage.includes("custom program error: 0x1774"))
        ) {
          throw new Error(
            `Insufficient available balance. ` +
            `You don't have enough available balance to withdraw this amount. ` +
            `Check your available balance in the affiliate dashboard.`
          )
        }
        
        // If .rpc() fails (e.g., wallet signing issue), try manual signing
        if (rpcError.message?.includes("sign") || rpcError.message?.includes("Wallet")) {
          console.log("RPC signing failed, trying manual signing...")
          const { Transaction } = await import("@solana/web3.js")
          const withdrawIx = await this.program.methods
            .withdrawRewards(new BN(amount))
            .accounts({
              affiliate,
              affiliateAccount: affiliatePDA,
              withdrawalVault: vault,
              vaultTokenAccount: vaultATA,
              affiliateTokenAccount: affiliateATA,
              tokenProgram: TOKEN_PROGRAM_ID,
              systemProgram: SYSTEM_PROGRAM_ID,
            } as any)
            .instruction()
          
          const tx = new Transaction().add(withdrawIx)
          const { blockhash } = await this.connection.getLatestBlockhash("confirmed")
          tx.recentBlockhash = blockhash
          tx.feePayer = affiliate
          
          if (!this._wallet?.signTransaction) {
            throw new Error("Wallet signTransaction method not available")
          }
          const signed = await this._wallet.signTransaction(tx)
          const signature = await this.connection.sendRawTransaction(signed.serialize(), {
            skipPreflight: false,
            maxRetries: 3,
          })
          await this.connection.confirmTransaction(signature, "confirmed")
          return signature
        }
        throw rpcError
      }
    } catch (err: any) {
      // Log the full error first for debugging
      console.error("🔍 Withdrawal error details:", {
        message: err.message,
        code: err.code,
        errorCode: err.errorCode,
        error: err.error,
        logs: err.logs,
        fullError: JSON.stringify(err, null, 2)
      })
      
      // Check for specific error types
      const errorMessage = err.message || ""
      const errorLogs = err.logs || []
      const allLogs = errorLogs.join(" ")
      
      // Check Anchor error structure - Anchor errors have err.error.errorCode.code
      const anchorErrorCode = err.error?.errorCode?.code || err.code || err.errorCode
      const anchorErrorName = err.error?.errorCode?.name || err.error?.name || err.name
      
      // Check if it's an affiliate balance error (error code 6004 = InsufficientBalance)
      // This is checked FIRST because it's more specific
      if (
        anchorErrorCode === 6004 ||
        anchorErrorName === "InsufficientBalance" ||
        anchorErrorName === "insufficientBalance" ||
        errorMessage.includes("InsufficientBalance") ||
        errorMessage.includes("insufficient balance") ||
        allLogs.includes("InsufficientBalance") ||
        allLogs.includes("insufficient balance") ||
        (errorMessage.includes("6004") && errorMessage.includes("balance")) ||
        (errorMessage.includes("custom program error: 0x1774")) // 6004 in hex
      ) {
        // This is about the affiliate's available balance, not the vault
        console.error("❌ Affiliate balance insufficient")
        throw new Error(
          `Insufficient available balance. ` +
          `You don't have enough available balance to withdraw this amount. ` +
          `Check your available balance in the affiliate dashboard.`
        )
      }
      
      // Check if it's specifically about vault funds (from token transfer failure)
      // Token program errors usually have different error codes (not 6004)
      // Only show vault error if it's NOT an InsufficientBalance error
      if (
        errorMessage.includes("insufficient funds") ||
        allLogs.includes("insufficient funds")
      ) {
        // Double-check it's not actually an InsufficientBalance error
        if (!errorMessage.includes("InsufficientBalance") && !allLogs.includes("InsufficientBalance")) {
          throw new Error(
            `Insufficient funds in withdrawal vault. ` +
            `The vault does not have enough IRMA tokens to fulfill this withdrawal. ` +
            `Please contact the platform administrator to fund the withdrawal vault.`
          )
        }
      }
      
      // Re-throw other errors as-is with original message
      throw err
    }
  }

  async fundWithdrawalVault(
    funder: PublicKey,
    amount: number,
    mint: PublicKey,
  ): Promise<string> {
    await this.ensureInitialized()
    const vault = this.getWithdrawalVault()
    const funderATA = await getAssociatedTokenAddress(mint, funder)
    const vaultATA = await getAssociatedTokenAddress(mint, vault, true)

    const tx = await this.program.methods
      .fundWithdrawalVault(new BN(amount))
      .accounts({
        funder,
        funderTokenAccount: funderATA,
        vaultTokenAccount: vaultATA,
        tokenProgram: TOKEN_PROGRAM_ID,
      } as any)
      .rpc()
    return tx
  }

  // ACCOUNT FETCHING (using manual deserialization if accounts namespace is disabled)
  async getSubscription(subscriber: PublicKey): Promise<any> {
    await this.ensureInitialized()
    const subscriptionPDA = this.getSubscriptionPDA(subscriber)
    try {
      return await this.program.account.subscription.fetch(subscriptionPDA)
    } catch (err) {
      // Fallback to manual deserialization if accounts namespace is disabled
      if ((this as any)._idl) {
        const accountInfo = await this.connection.getAccountInfo(subscriptionPDA)
        if (!accountInfo) return null
        // Manual deserialization would go here
        throw new Error("Manual deserialization not implemented. Accounts namespace is disabled.")
      }
      throw err
    }
  }

  async getSubscriptionPlan(platformId: string): Promise<any> {
    await this.ensureInitialized()
    const planPDA = this.getPlanPDA(platformId)
    try {
      return await this.program.account.subscriptionPlan.fetch(planPDA)
    } catch (err) {
      throw err
    }
  }

  async getAffiliateAccount(affiliate: PublicKey): Promise<any> {
    await this.ensureInitialized()
    const affiliatePDA = this.getAffiliateAccountPDA(affiliate)
    try {
      const account = await this.program.account.affiliateAccount.fetch(affiliatePDA)
      
      // Handle both camelCase and snake_case field names (Anchor converts snake_case to camelCase)
      // But the IDL uses snake_case, so check both
      const totalCredits = account.totalCredits || account.total_credits
      const totalWithdrawn = account.totalWithdrawn || account.total_withdrawn || account["total_withdrawn"]
      let availableBalance = account.availableBalance || account.available_balance || account["available_balance"]
      
      // Helper to convert BN to number
      const toNumber = (bn: any): number => {
        if (!bn) return 0
        if (typeof bn.toNumber === 'function') return bn.toNumber()
        if (typeof bn === 'number') return bn
        return parseFloat(String(bn)) || 0
      }
      
      const totalCreditsNum = toNumber(totalCredits)
      const totalWithdrawnNum = toNumber(totalWithdrawn)
      const availableBalanceNum = toNumber(availableBalance)
      
      // Migration: If available_balance is 0 but total_credits > 0, 
      // this is likely an old account. Set available_balance = total_credits - total_withdrawn
      if (availableBalanceNum === 0 && totalCreditsNum > 0) {
        const migratedBalance = totalCreditsNum - totalWithdrawnNum
        // Create a BN-like object for compatibility
        availableBalance = {
          toString: () => Math.max(0, migratedBalance).toString(),
          toNumber: () => Math.max(0, migratedBalance),
        }
        console.log("🔄 Migrated old affiliate account in getAffiliateAccount:", {
          totalCredits: totalCreditsNum,
          totalWithdrawn: totalWithdrawnNum,
          migratedAvailableBalance: Math.max(0, migratedBalance),
        })
      }
      
      // Log the raw account to see what's actually there
      console.log("📊 Fetched affiliate account from on-chain:", {
        affiliate: account.affiliate?.toString(),
        totalCredits: totalCredits?.toString() || totalCredits?.toNumber()?.toString() || "0",
        totalWithdrawn: totalWithdrawn?.toString() || totalWithdrawn?.toNumber()?.toString() || "0",
        availableBalance: availableBalance?.toString() || availableBalance?.toNumber()?.toString() || "0",
        totalCreditsValue: totalCredits,
        totalWithdrawnValue: totalWithdrawn,
        availableBalanceValue: availableBalance,
        accountKeys: Object.keys(account),
      })
      
      // Return account with migrated availableBalance
      return {
        ...account,
        availableBalance,
        available_balance: availableBalance,
      }
    } catch (err: any) {
      // If deserialization fails, the account might be using the old structure (without available_balance)
      if (err.message?.includes("AccountDidNotDeserialize") || err.code === 3003) {
        console.warn("⚠️  Account uses old structure (missing available_balance field)")
        console.warn("   Attempting to read raw account data and migrate...")
        
        // Try to read raw account data
        try {
          const accountInfo = await this.connection.getAccountInfo(affiliatePDA)
          if (accountInfo) {
            const data = accountInfo.data
            // Old structure: 8 (discriminator) + 32 (affiliate) + 8 (total_credits) + 8 (total_withdrawn) = 56 bytes
            // New structure: 8 + 32 + 8 + 8 + 8 (available_balance) = 64 bytes
            if (data.length === 56) {
              // Old structure - parse manually
              const affiliatePubkey = new PublicKey(data.slice(8, 40))
              const totalCreditsBN = data.readBigUInt64LE(40)
              const totalWithdrawnBN = data.readBigUInt64LE(48)
              
              const totalCreditsNum = Number(totalCreditsBN)
              const totalWithdrawnNum = Number(totalWithdrawnBN)
              
              // For old accounts: available_balance = total_credits - total_withdrawn
              const availableBalanceNum = Math.max(0, totalCreditsNum - totalWithdrawnNum)
              
              console.log("📊 Parsed old account structure (migrated):", {
                affiliate: affiliatePubkey.toString(),
                totalCredits: totalCreditsNum.toString(),
                totalWithdrawn: totalWithdrawnNum.toString(),
                availableBalance: availableBalanceNum.toString() + " (migrated from total_credits - total_withdrawn)",
              })
              
              // Return a compatible object with BN-like methods
              const bnLike = (num: number) => ({
                toString: () => num.toString(),
                toNumber: () => num,
              })
              
              return {
                affiliate: affiliatePubkey,
                totalCredits: bnLike(totalCreditsNum),
                totalWithdrawn: bnLike(totalWithdrawnNum),
                availableBalance: bnLike(availableBalanceNum),
                // For compatibility with snake_case
                total_credits: bnLike(totalCreditsNum),
                total_withdrawn: bnLike(totalWithdrawnNum),
                available_balance: bnLike(availableBalanceNum),
              }
            } else {
              throw new Error(`Unexpected account size: ${data.length} bytes`)
            }
          } else {
            throw new Error("Account not found")
          }
        } catch (rawErr: any) {
          console.error("Could not read raw account data:", rawErr)
          throw new Error(
            "Affiliate account uses old structure and could not be migrated. " +
            "Please re-initialize your affiliate account."
          )
        }
      }
      console.error("Error fetching affiliate account:", err)
      throw err
    }
  }

  async getSubscriptionVaultInfo(): Promise<any> {
    await this.ensureInitialized()
    const vault = this.getSubscriptionVault()
    try {
      return await this.program.account.subscriptionVault.fetch(vault)
    } catch (err: any) {
      // Check if account exists on-chain
      const accountInfo = await this.connection.getAccountInfo(vault)
      if (!accountInfo) {
        const error = new Error("AccountNotInitialized: Subscription vault is not initialized")
        ;(error as any).code = 3012
        throw error
      }
      throw err
    }
  }

  async isSubscriptionVaultInitialized(): Promise<boolean> {
    try {
      await this.getSubscriptionVaultInfo()
      return true
    } catch (err: any) {
      if (err.message?.includes("AccountNotInitialized") || err.code === 3012) {
        return false
      }
      // Re-throw other errors
      throw err
    }
  }

  async getWithdrawalVaultInfo(): Promise<any> {
    await this.ensureInitialized()
    const vault = this.getWithdrawalVault()
    try {
      return await this.program.account.withdrawalVault.fetch(vault)
    } catch (err) {
      throw err
    }
  }

  // ==================== STAKING METHODS ====================

  // Get Staking Vault PDA
  getStakingVault(): PublicKey {
    const programId = this.getProgramId()
    const [vault] = PublicKey.findProgramAddressSync(
      [Buffer.from("staking_vault")],
      programId
    )
    return vault
  }

  // Get Staking Config PDA
  getStakingConfig(): PublicKey {
    const programId = this.getProgramId()
    const [config] = PublicKey.findProgramAddressSync(
      [Buffer.from("staking_config")],
      programId
    )
    return config
  }

  // Get Staking Account PDA
  getStakingAccountPDA(staker: PublicKey, tier: number, nonce: number = 0): PublicKey {
    const programId = this.getProgramId()
    const nonceBuffer = new BN(nonce).toArrayLike(Buffer, "le", 8)
    const [stakingAccount] = PublicKey.findProgramAddressSync(
      [
        Buffer.from("staking"),
        staker.toBuffer(),
        Buffer.from([tier]),
        nonceBuffer,
      ],
      programId
    )
    return stakingAccount
  }

  // Get a single staking account by nonce
  async getStakingAccount(staker: PublicKey, tier: number, nonce?: number): Promise<any> {
    await this.ensureInitialized()
    try {
      const stakingAccountPDA = this.getStakingAccountPDA(staker, tier, nonce || 0)
      return await this.program.account.stakingAccount.fetch(stakingAccountPDA)
    } catch (error: any) {
      if (error.message?.includes("Account does not exist") || error.code === 3012) {
        return null
      }
      throw error
    }
  }

  // Get all staking accounts for a user using getProgramAccounts
  async getAllStakingAccounts(staker: PublicKey): Promise<any[]> {
    await this.ensureInitialized()
    try {
      // StakingAccount layout:
      // 8 (discriminator) + 32 (staker) + 8 (amount_staked) + 8 (nonce) +
      // 1 (stake_tier) + 8 (lock_start) + 8 (lock_end) + 8 (rewards_earned) +
      // 8 (rewards_claimed) + 1 (is_active) + 1 (bump) = 91
      const STAKING_ACCOUNT_SIZE = 91

      console.log("Fetching staking accounts for:", staker.toBase58())
      console.log("Program ID:", this.program.programId.toBase58())

      const accounts = await this.connection.getProgramAccounts(
        this.program.programId,
        {
          filters: [
            { dataSize: STAKING_ACCOUNT_SIZE },
            { memcmp: { offset: 8, bytes: staker.toBase58() } },
          ],
        }
      )

      console.log("Found staking accounts:", accounts.length)

      return accounts.map(({ pubkey, account }) => {
        try {
          // Try Anchor coder first
          const decoded = this.program.coder.accounts.decode("StakingAccount", account.data)
          console.log("Decoded staking account:", pubkey.toBase58(), decoded)
          return { ...decoded, publicKey: pubkey }
        } catch (decodeErr) {
          // Fallback: manual deserialization
          console.log("Anchor decode failed, using manual deserialization:", decodeErr)
          try {
            const data = account.data
            const view = new DataView(data.buffer, data.byteOffset, data.byteLength)
            const stakerKey = new PublicKey(data.slice(8, 40))

            // Read u64 as two u32s (little-endian) for browser compat
            const readU64 = (offset: number): number => {
              const lo = view.getUint32(offset, true)
              const hi = view.getUint32(offset + 4, true)
              return lo + hi * 4294967296
            }
            const readI64 = (offset: number): number => {
              const lo = view.getUint32(offset, true)
              const hi = view.getInt32(offset + 4, true)
              return lo + hi * 4294967296
            }

            const amountStaked = readU64(40)
            const nonce = readU64(48)
            const stakeTierByte = data[56]
            const lockStart = readI64(57)
            const lockEnd = readI64(65)
            const rewardsEarned = readU64(73)
            const rewardsClaimed = readU64(81)
            const isActive = data[89] === 1
            const bump = data[90]

            const tierNames = ["bronze", "silver", "gold", "platinum"]
            const tierName = tierNames[stakeTierByte] || "bronze"
            const stakeTier = { [tierName]: {} }

            const result = {
              staker: stakerKey,
              amountStaked: amountStaked.toString(),
              nonce: nonce.toString(),
              stakeTier,
              lockStart,
              lockEnd,
              rewardsEarned: rewardsEarned.toString(),
              rewardsClaimed: rewardsClaimed.toString(),
              isActive,
              bump,
              publicKey: pubkey,
            }
            console.log("Manually decoded staking account:", pubkey.toBase58(), result)
            return result
          } catch (manualErr) {
            console.error("Manual deserialization failed:", manualErr)
            return null
          }
        }
      }).filter(Boolean)
    } catch (error: any) {
      console.error("Error fetching all staking accounts:", error)
      return []
    }
  }

  // Get staking config
  async fetchStakingConfig(): Promise<any> {
    await this.ensureInitialized()
    try {
      const configPDA = this.getStakingConfig()
      return await this.program.account.stakingConfig.fetch(configPDA)
    } catch (error: any) {
      throw error
    }
  }

  // Initialize staking vault (admin only)
  async initializeStakingVault(authority: PublicKey): Promise<string> {
    await this.ensureInitialized()
    const stakingVaultPDA = this.getStakingVault()
    
    const tx = await this.program.methods
      .initializeStakingVault()
      .accounts({
        stakingVault: stakingVaultPDA,
        authority,
        systemProgram: SystemProgram.programId,
      } as any)
      .rpc()
    return tx
  }

  // Initialize staking config (admin only)
  async initializeStakingConfig(authority: PublicKey): Promise<string> {
    await this.ensureInitialized()
    const stakingConfigPDA = this.getStakingConfig()
    
    const tx = await this.program.methods
      .initializeStakingConfig()
      .accounts({
        stakingConfig: stakingConfigPDA,
        authority,
        systemProgram: SystemProgram.programId,
      } as any)
      .rpc()
    return tx
  }

  // Stake IRMA tokens
  async stake(
    staker: PublicKey,
    amount: number,
    tier: number, // 0 = Bronze, 1 = Silver, 2 = Gold, 3 = Platinum
    mint: PublicKey,
    nonce: number = Date.now(),
  ): Promise<string> {
    await this.ensureInitialized()
    console.log("Stake function called with:", { staker: staker.toString(), amount, tier, mint: mint.toString(), nonce })

    const stakingAccountPDA = this.getStakingAccountPDA(staker, tier, nonce)
    const stakingConfigPDA = this.getStakingConfig()
    const stakingVaultPDA = this.getStakingVault()
    
    console.log("PDAs:", {
      stakingAccount: stakingAccountPDA.toString(),
      stakingConfig: stakingConfigPDA.toString(),
      stakingVault: stakingVaultPDA.toString()
    })
    
    const stakerATA = await getAssociatedTokenAddress(mint, staker)
    const vaultATA = await getAssociatedTokenAddress(mint, stakingVaultPDA, true)

    console.log("Token accounts:", {
      stakerATA: stakerATA.toString(),
      vaultATA: vaultATA.toString()
    })

    // Check if vault ATA exists, create if not
    try {
      await getAccount(this.connection, vaultATA)
      console.log("Vault token account already exists")
    } catch (error: any) {
      if (error.name === "TokenAccountNotFoundError" || error.message?.includes("could not find account")) {
        console.log("Creating vault token account...")
        const createATAInstruction = createAssociatedTokenAccountInstruction(
          staker, // payer
          vaultATA, // account to create
          stakingVaultPDA, // owner (vault PDA)
          mint // mint
        )
        
        // Create a transaction with just the ATA creation
        const createTx = new Transaction().add(createATAInstruction)
        
        // Get recent blockhash before signing
        const { blockhash } = await this.connection.getLatestBlockhash("confirmed")
        createTx.recentBlockhash = blockhash
        createTx.feePayer = staker
        
        // Sign and send the transaction
        if (this._wallet && this._wallet.signTransaction) {
          const signedTx = await this._wallet.signTransaction(createTx)
          const signature = await this.connection.sendRawTransaction(signedTx.serialize(), {
            skipPreflight: false,
            preflightCommitment: "confirmed",
          })
          await this.connection.confirmTransaction(signature, "confirmed")
          console.log("Vault token account created successfully:", signature)
        } else {
          throw new Error("Wallet signTransaction not available")
        }
      } else {
        throw error
      }
    }

    // Convert amount to smallest unit (9 decimals)
    const amountScaled = Math.floor(amount * 1_000_000_000)
    console.log("Amount scaled:", amountScaled)

    // Map tier number to enum variant
    const tierEnum = [
      { bronze: {} },
      { silver: {} },
      { gold: {} },
      { platinum: {} },
    ][tier]
    
    console.log("Tier enum:", tierEnum)
    console.log("Building transaction...")

    try {
      const tx = await this.program.methods
        .stake(new BN(amountScaled), tierEnum, new BN(nonce))
        .accounts({
          staker,
          stakingAccount: stakingAccountPDA,
          stakingConfig: stakingConfigPDA,
          stakingVault: stakingVaultPDA,
          stakerTokenAccount: stakerATA,
          vaultTokenAccount: vaultATA,
          tokenProgram: TOKEN_PROGRAM_ID,
          systemProgram: SystemProgram.programId,
        } as any)
        .rpc()
      console.log("Transaction sent, signature:", tx)
      return tx
    } catch (error: any) {
      console.error("Error in stake RPC call:", error)
      throw error
    }
  }

  // Unstake IRMA tokens
  async unstake(
    staker: PublicKey,
    tier: number,
    early: boolean,
    mint: PublicKey,
    nonce: number,
  ): Promise<string> {
    await this.ensureInitialized()
    const stakingAccountPDA = this.getStakingAccountPDA(staker, tier, nonce)
    const stakingConfigPDA = this.getStakingConfig()
    const stakingVaultPDA = this.getStakingVault()

    const stakerATA = await getAssociatedTokenAddress(mint, staker)
    const vaultATA = await getAssociatedTokenAddress(mint, stakingVaultPDA, true)

    const tx = await this.program.methods
      .unstake(early, new BN(nonce))
      .accounts({
        staker,
        stakingAccount: stakingAccountPDA,
        stakingConfig: stakingConfigPDA,
        stakingVault: stakingVaultPDA,
        stakerTokenAccount: stakerATA,
        vaultTokenAccount: vaultATA,
        tokenProgram: TOKEN_PROGRAM_ID,
        systemProgram: SystemProgram.programId,
      } as any)
      .rpc()
    return tx
  }

  // Claim rewards
  async claimRewards(
    staker: PublicKey,
    tier: number,
    mint: PublicKey,
    nonce: number,
  ): Promise<string> {
    await this.ensureInitialized()
    const stakingAccountPDA = this.getStakingAccountPDA(staker, tier, nonce)
    const stakingConfigPDA = this.getStakingConfig()
    const stakingVaultPDA = this.getStakingVault()

    const stakerATA = await getAssociatedTokenAddress(mint, staker)
    const vaultATA = await getAssociatedTokenAddress(mint, stakingVaultPDA, true)

    const tx = await this.program.methods
      .claimRewards(new BN(nonce))
      .accounts({
        staker,
        stakingAccount: stakingAccountPDA,
        stakingConfig: stakingConfigPDA,
        stakingVault: stakingVaultPDA,
        stakerTokenAccount: stakerATA,
        vaultTokenAccount: vaultATA,
        tokenProgram: TOKEN_PROGRAM_ID,
        systemProgram: SystemProgram.programId,
      } as any)
      .rpc()
    return tx
  }
}

